import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Calendar, Users, User, Plane, Briefcase } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { projetsService, rattachementsService, vacancesService, tempsPlanifiesService } from '../services/api';
import { format, parseISO, startOfWeek, endOfWeek, eachWeekOfInterval, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import VacancesModal from '../components/VacancesModal';
import RattachementModal from '../components/RattachementModal';
import ProjetRattachementModal from '../components/ProjetRattachementModal';

const CapacityPlanning = () => {
  const { state } = useApp();
  const [loading, setLoading] = useState(true);
  const [projetsWithRattachements, setProjetsWithRattachements] = useState([]);
  const [personnesData, setPersonnesData] = useState([]);
  const [vacancesData, setVacancesData] = useState({});
  const [tempsPlanifiesData, setTempsPlanifiesData] = useState({});
  const [showVacancesModal, setShowVacancesModal] = useState(false);
  const [selectedPersonneForVacances, setSelectedPersonneForVacances] = useState(null);
  const [showRattachementModal, setShowRattachementModal] = useState(false);
  const [selectedPersonneForRattachement, setSelectedPersonneForRattachement] = useState(null);
  const [showProjetRattachementModal, setShowProjetRattachementModal] = useState(false);
  const [selectedProjetForRattachement, setSelectedProjetForRattachement] = useState(null);
  
  // Configuration des dimensions
  const COLUMN_WIDTH = 160; // Largeur fixe de chaque colonne de semaine en px
  const SIDEBAR_WIDTH = 320; // Largeur fixe de la colonne des personnes en px
  
  // Refs et état pour le drag scroll
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, scrollLeft: 0 });
  const headerScrollRef = useRef(null);
  
  // Mémoriser les dates de période pour éviter les recalculs en boucle
  const { startDate, endDate, weeksInPeriod } = useMemo(() => {
    const periodSelection = state.periodSelection;
    const now = new Date();
    
    let start, end;
    
    if (periodSelection.type === 'preset') {
      switch (periodSelection.preset) {
        case 'current_month':
          start = startOfMonth(now);
          end = endOfMonth(now);
          break;
        case 'current_quarter':
          start = startOfQuarter(now);
          end = endOfQuarter(now);
          break;
        case 'fiscal_year': {
          // Exercice en cours : du 1er juillet dernier au 30 juin prochain
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth(); // 0-11
          
          // Si nous sommes avant le 1er juillet (mois 6), l'exercice a commencé l'année dernière
          if (currentMonth < 6) {
            start = new Date(currentYear - 1, 6, 1); // 1er juillet année dernière
            end = new Date(currentYear, 5, 30); // 30 juin année courante
          } else {
            // Si nous sommes le 1er juillet ou après, l'exercice a commencé cette année
            start = new Date(currentYear, 6, 1); // 1er juillet année courante
            end = new Date(currentYear + 1, 5, 30); // 30 juin année prochaine
          }
          break;
        }
        case '6_months':
          start = startOfMonth(now);
          end = endOfMonth(new Date(now.getFullYear(), now.getMonth() + 5, now.getDate()));
          break;
        default:
          start = startOfMonth(now);
          end = endOfMonth(now);
      }
    } else if (periodSelection.type === 'custom') {
      start = periodSelection.startDate ? new Date(periodSelection.startDate) : startOfMonth(now);
      end = periodSelection.endDate ? new Date(periodSelection.endDate) : endOfMonth(now);
    } else if (periodSelection.type === 'single') {
      const date = periodSelection.singleDate ? new Date(periodSelection.singleDate) : now;
      start = date;
      end = date;
    } else {
      start = startOfMonth(now);
      end = endOfMonth(now);
    }
    
    // Calculer les semaines dans l'intervalle (en commençant par lundi)
    const weeks = eachWeekOfInterval({ start, end }, { weekStartsOn: 1 });
    
    return { startDate: start, endDate: end, weeksInPeriod: weeks };
  }, [state.periodSelection]); // On veut recalculer quand la période change

  const loadCapacityData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Utiliser les dates mémorisées de la période
      
      // Récupérer les projets avec les rattachements
      const projetsResponse = await projetsService.getAll();
      const projets = projetsResponse.data || [];
      
      // Récupérer tous les rattachements
      const rattachementsResponse = await rattachementsService.getAll();
      const rattachements = rattachementsResponse.data || [];
      
      // Créer un map des rattachements par projet
      const rattachementsByProjet = rattachements.reduce((acc, rattachement) => {
        if (!acc[rattachement.id_projet]) {
          acc[rattachement.id_projet] = [];
        }
        acc[rattachement.id_projet].push(rattachement);
        return acc;
      }, {});
      
      // Enrichir les projets avec leurs rattachements
      const enrichedProjets = projets.map(projet => ({
        ...projet,
        rattachements: rattachementsByProjet[projet.id] || []
      }));
      
      setProjetsWithRattachements(enrichedProjets);
      
      // Créer la structure des données personnes avec leurs affectations
      const personnes = state.personnes?.data || [];
      const personnesWithAssignments = personnes.filter(p => p.actif).map(personne => {
        const assignments = rattachements.filter(r => r.id_personne === personne.id);
        const assignmentsWithProjects = assignments.map(assignment => {
          const projet = projets.find(p => p.id === assignment.id_projet);
          return {
            ...assignment,
            projet
          };
        });
        
        return {
          ...personne,
          assignments: assignmentsWithProjects
        };
      });
      
      setPersonnesData(personnesWithAssignments);
      
      // Charger les vacances pour chaque personne active
      const vacancesPromises = personnesWithAssignments.map(async (personne) => {
        try {
          const vacancesResponse = await vacancesService.getByPersonne(personne.id, {
            date_debut: format(startDate, 'yyyy-MM-dd'),
            date_fin: format(endDate, 'yyyy-MM-dd')
          });
          return {
            personneId: personne.id,
            vacances: vacancesResponse.data || []
          };
        } catch (error) {
          console.error(`Erreur lors du chargement des vacances pour ${personne.prenom} ${personne.nom}:`, error);
          return {
            personneId: personne.id,
            vacances: []
          };
        }
      });

      const vacancesResults = await Promise.all(vacancesPromises);
      const vacancesMap = vacancesResults.reduce((acc, { personneId, vacances }) => {
        acc[personneId] = vacances;
        return acc;
      }, {});
      
      setVacancesData(vacancesMap);
      
      // Charger les temps planifiés pour chaque projet sur la période
      const tempsPlanifiesPromises = enrichedProjets.map(async (projet) => {
        try {
          const tempsPlanifiesResponse = await tempsPlanifiesService.getByProjet(projet.id);
          return {
            projetId: projet.id,
            tempsPlanifies: tempsPlanifiesResponse.data?.temps_planifies || []
          };
        } catch (error) {
          console.error(`Erreur lors du chargement des temps planifiés pour le projet ${projet.nom}:`, error);
          return {
            projetId: projet.id,
            tempsPlanifies: []
          };
        }
      });

      const tempsPlanifiesResults = await Promise.all(tempsPlanifiesPromises);
      const tempsPlanifiesMap = tempsPlanifiesResults.reduce((acc, { projetId, tempsPlanifies }) => {
        acc[projetId] = tempsPlanifies;
        return acc;
      }, {});
      
      setTempsPlanifiesData(tempsPlanifiesMap);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données de capacity planning:', error);
    } finally {
      setLoading(false);
    }
  }, [state.personnes?.data, startDate, endDate]);

  useEffect(() => {
    loadCapacityData();
  }, [loadCapacityData, state.periodSelection]); // Recharger quand la période change

  const handleVacancesClick = (personne) => {
    setSelectedPersonneForVacances(personne);
    setShowVacancesModal(true);
  };

  const handleCloseVacancesModal = () => {
    setShowVacancesModal(false);
    setSelectedPersonneForVacances(null);
  };

  const handleRattachementClick = (personne) => {
    setSelectedPersonneForRattachement(personne);
    setShowRattachementModal(true);
  };

  const handleCloseRattachementModal = () => {
    setShowRattachementModal(false);
    setSelectedPersonneForRattachement(null);
  };

  const handleProjetRattachementClick = (projet) => {
    setSelectedProjetForRattachement(projet);
    setShowProjetRattachementModal(true);
  };

  const handleCloseProjetRattachementModal = () => {
    setShowProjetRattachementModal(false);
    setSelectedProjetForRattachement(null);
  };

  // Fonctions de gestion du drag scroll
  const handleMouseDown = useCallback((e) => {
    // Ne pas déclencher le drag si on clique sur un bouton ou dans une modale
    if (
      e.target.closest('button') || 
      e.target.closest('.fixed') || // Les modales ont une classe fixed
      e.target.closest('select') ||
      e.target.closest('input') ||
      e.target.closest('textarea')
    ) {
      return;
    }

    isDragging.current = true;
    dragStart.current = {
      x: e.pageX,
      scrollLeft: headerScrollRef.current?.scrollLeft || 0
    };
    document.body.style.cursor = 'grabbing';
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || !headerScrollRef.current) return;
    
    const x = e.pageX;
    const walk = (x - dragStart.current.x) * 1.5; // Sensibilité du drag
    const newScrollLeft = dragStart.current.scrollLeft - walk;
    
    // Synchroniser le scroll sur l'en-tête et tous les corps
    headerScrollRef.current.scrollLeft = newScrollLeft;
    const bodyScrolls = document.querySelectorAll('.calendar-body-scroll');
    bodyScrolls.forEach(bodyScroll => {
      bodyScroll.scrollLeft = newScrollLeft;
    });
    
    e.preventDefault();
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    document.body.style.cursor = 'default';
  }, []);

  // Ajouter les event listeners pour le drag global
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Ajouter les styles CSS pour cacher les scrollbars
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .calendar-body-scroll::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

    // Calculer la charge par semaine pour une personne
  const calculateChargeParSemaine = (personne) => {
    const points = [];
    const backgroundColors = [];
    const graphWidth = weeksInPeriod.length * COLUMN_WIDTH;
    const graphHeight = 50;
    
    weeksInPeriod.forEach((week, weekIndex) => {
      const weekStart = startOfWeek(week, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(week, { weekStartsOn: 1 });
      
      // Calculer la capacité disponible pour cette semaine
      const capaciteDisponible = 5; // 5 jours par semaine
      const indiceProductivite = parseFloat(personne.indice_productivite) || 0;
      const capaciteAvecProductivite = capaciteDisponible * indiceProductivite;
      
      // Calculer les vacances pour cette semaine
      const vacancesSemaine = vacancesData[personne.id] 
        ? vacancesData[personne.id].filter(v => {
            const vacancesWeekStart = startOfWeek(parseISO(v.semaine), { weekStartsOn: 1 });
            return vacancesWeekStart.getTime() === weekStart.getTime();
          }).reduce((total, v) => total + parseFloat(v.jours_vacances), 0)
        : 0;
      
      const capaciteNette = Math.max(0, capaciteAvecProductivite - vacancesSemaine);
      
      // Calculer la charge (jours rattachés) pour cette semaine
      const chargeSemaine = personne.assignments
        .filter(assignment => {
          if (!assignment.projet) return false;
          
          const assignmentStart = parseISO(assignment.date_debut || assignment.projet.date_debut);
          const assignmentEnd = parseISO(assignment.date_fin || assignment.projet.date_fin);
          
          // Vérifier si l'affectation couvre cette semaine
          return assignmentStart <= weekEnd && assignmentEnd >= weekStart;
        })
        .reduce((total, assignment) => {
          // Calculer les jours de cette affectation pour cette semaine
          const assignmentStart = parseISO(assignment.date_debut || assignment.projet.date_debut);
          const assignmentEnd = parseISO(assignment.date_fin || assignment.projet.date_fin);
          
          const effectiveStart = assignmentStart < weekStart ? weekStart : assignmentStart;
          const effectiveEnd = assignmentEnd > weekEnd ? weekEnd : assignmentEnd;
          
          // Calculer le nombre de jours ouvrables dans cette semaine
          const daysDiff = Math.ceil((effectiveEnd - effectiveStart) / (24 * 60 * 60 * 1000));
          const joursSemaine = Math.min(daysDiff, 5);
          
          return total + joursSemaine;
        }, 0);
      
      // Calculer le pourcentage de charge (peut aller jusqu'à 200%)
      const pourcentageCharge = capaciteNette > 0 ? (chargeSemaine / capaciteNette) * 100 : 0;
      
      // Position X dans le graphique
      const x = (weekIndex / (weeksInPeriod.length - 1)) * (graphWidth - 40) + 20;
      
      // Position Y dans le graphique (inversée car SVG Y=0 est en haut, échelle 0-200%)
      const y = graphHeight - 10 - (pourcentageCharge / 200) * (graphHeight - 20);
      
      // Calculer la couleur de fond basée sur la charge
      let backgroundColor;
      if (pourcentageCharge <= 100) {
        // Charge normale : bleu clair
        backgroundColor = '#dbeafe'; // blue-100
      } else {
        // Charge élevée : transition vers le rouge
        const redIntensity = Math.min(255, Math.floor((pourcentageCharge - 100) * 2.55));
        backgroundColor = `rgb(255, ${Math.max(0, 255 - redIntensity)}, ${Math.max(0, 255 - redIntensity)})`;
      }
      
      points.push(`${x},${y}`);
      backgroundColors.push({
        x: x,
        y: y,
        charge: pourcentageCharge,
        color: backgroundColor
      });
    });
    
    return { points: points.join(' '), backgroundColors };
  };

  // Calculer la capacité nette d'une personne pour la période
  const calculateCapaciteNette = (personne) => {
    const nombreSemaines = weeksInPeriod.length;
    const capaciteTheorique = nombreSemaines * 5; // 5 jours par semaine
    const indiceProductivite = parseFloat(personne.indice_productivite) || 0;
    const capaciteAvecProductivite = capaciteTheorique * indiceProductivite;
    
    // Calculer les jours de vacances pour cette personne sur la période
    const joursVacances = vacancesData[personne.id] 
      ? vacancesData[personne.id].reduce((total, v) => total + parseFloat(v.jours_vacances), 0)
      : 0;
    
    const capaciteNette = Math.max(0, capaciteAvecProductivite - joursVacances);
    
    return {
      capaciteTheorique,
      capaciteAvecProductivite,
      joursVacances,
      capaciteNette,
      pourcentageProductivite: indiceProductivite * 100
    };
  };

  // Calculer la capacité totale de l'équipe
  const calculateCapaciteTotale = () => {
    return personnesData.reduce((total, personne) => {
      const capacite = calculateCapaciteNette(personne);
      return {
        capaciteTheorique: total.capaciteTheorique + capacite.capaciteTheorique,
        capaciteAvecProductivite: total.capaciteAvecProductivite + capacite.capaciteAvecProductivite,
        joursVacances: total.joursVacances + capacite.joursVacances,
        capaciteNette: total.capaciteNette + capacite.capaciteNette
      };
    }, { capaciteTheorique: 0, capaciteAvecProductivite: 0, joursVacances: 0, capaciteNette: 0 });
  };

  // Calculer la position et la largeur des barres de projet
  const getProjectBarStyle = (dateDebut, dateFin) => {
    const totalWeeks = weeksInPeriod.length;
    
    const projectStart = parseISO(dateDebut);
    const projectEnd = parseISO(dateFin);
    
    // Si le projet ne chevauche pas avec la période affichée
    if (projectEnd < startDate || projectStart > endDate) {
      return { display: 'none' };
    }
    
    // Calculer les semaines effectives dans la période affichée
    const effectiveStart = projectStart < startDate ? startDate : projectStart;
    const effectiveEnd = projectEnd > endDate ? endDate : projectEnd;
    
    // Trouver la semaine de début dans la liste des semaines
    const effectiveStartWeek = startOfWeek(effectiveStart, { weekStartsOn: 1 });
    const startWeekIndex = weeksInPeriod.findIndex(week => 
      week.getTime() === effectiveStartWeek.getTime()
    );
    
    // Si la semaine de début n'est pas trouvée, calculer manuellement
    const weekStartIndex = startWeekIndex >= 0 ? startWeekIndex : 
      Math.floor((effectiveStartWeek - startOfWeek(startDate, { weekStartsOn: 1 })) / (7 * 24 * 60 * 60 * 1000));
    
    const left = (weekStartIndex / totalWeeks) * 100;
    
    // Calculer le nombre de semaines couvertes par le projet
    const effectiveEndWeek = startOfWeek(effectiveEnd, { weekStartsOn: 1 });
    const weeksDiff = Math.max(1, Math.ceil((effectiveEndWeek - effectiveStartWeek) / (7 * 24 * 60 * 60 * 1000)) + 1);
    const width = (weeksDiff / totalWeeks) * 100;
    
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.min(100 - Math.max(0, left), width)}%`
    };
  };

  // Calculer la position et la largeur des barres de vacances
  const getVacancesBarStyle = (semaine, joursVacances) => {
    const totalWeeks = weeksInPeriod.length;
    
    // Convertir la semaine (date du lundi) en objet Date
    const weekStart = startOfWeek(parseISO(semaine), { weekStartsOn: 1 });
    
    // Si la semaine ne chevauche pas avec la période affichée
    if (weekStart < startOfWeek(startDate, { weekStartsOn: 1 }) || weekStart > startOfWeek(endDate, { weekStartsOn: 1 })) {
      return { display: 'none' };
    }
    
    // Trouver l'index de cette semaine dans notre liste
    const weekIndex = weeksInPeriod.findIndex(week => 
      week.getTime() === weekStart.getTime()
    );
    
    if (weekIndex === -1) {
      return { display: 'none' };
    }
    
    // Calculer la position (une semaine = 100% / nombre de semaines)
    const left = (weekIndex / totalWeeks) * 100;
    const width = (1 / totalWeeks) * 100;
    
    return {
      left: `${left}%`,
      width: `${width}%`
    };
  };

  // Obtenir la couleur d'un projet selon son statut
  const getProjectColor = (statut) => {
    switch (statut) {
      case 'planifie': return 'bg-blue-400';
      case 'en_cours': return 'bg-green-400';
      case 'termine': return 'bg-gray-400';
      case 'suspendu': return 'bg-red-400';
      default: return 'bg-gray-400';
    }
  };

  // Obtenir la couleur d'une phase de projet
  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'analyse_design': return 'bg-purple-400';
      case 'build': return 'bg-blue-400';
      case 'test': return 'bg-green-400';
      default: return 'bg-gray-400';
    }
  };

  // Calculer les temps planifiés pour une phase donnée d'un rattachement
  const calculateTempsPlanifiesForPhase = (assignment, phase) => {
    if (!assignment.projet || !tempsPlanifiesData[assignment.projet.id]) {
      return 0;
    }

    const tempsPlanifies = tempsPlanifiesData[assignment.projet.id];
    
    // Filtrer les temps planifiés dans la période du rattachement
    const assignmentStart = parseISO(assignment.date_debut || assignment.projet.date_debut);
    const assignmentEnd = parseISO(assignment.date_fin || assignment.projet.date_fin);
    
    const tempsDansLaPeriode = tempsPlanifies.filter(temps => {
      const semaine = parseISO(temps.semaine);
      return semaine >= assignmentStart && semaine <= assignmentEnd;
    });

    // Sommer les temps de la phase correspondante
    return tempsDansLaPeriode.reduce((total, temps) => {
      const tempsPhase = parseFloat(temps[phase]) || 0;
      return total + tempsPhase;
    }, 0);
  };

  // Fonction pour détecter si deux périodes se chevauchent
  const periodsOverlap = (start1, end1, start2, end2) => {
    return start1 <= end2 && start2 <= end1;
  };

  // Fonction pour calculer les positions des barres sans chevauchement
  const calculateBarPositions = (bars, startOffset = 0) => {
    if (!bars || bars.length === 0) return [];

    // Trier les barres par date de début
    const sortedBars = [...bars].sort((a, b) => {
      const startA = parseISO(a.startDate);
      const startB = parseISO(b.startDate);
      return startA - startB;
    });

    const positions = [];
    const levels = []; // Suivre les niveaux occupés

    sortedBars.forEach((bar, index) => {
      const barStart = parseISO(bar.startDate);
      const barEnd = parseISO(bar.endDate);
      
      // Trouver le niveau le plus bas disponible pour cette barre
      let level = 0;
      let levelFound = false;
      
      while (!levelFound) {
        // Vérifier si ce niveau est libre pour cette période
        const levelFree = !levels[level] || levels[level].every(existingBar => {
          const existingStart = parseISO(existingBar.startDate);
          const existingEnd = parseISO(existingBar.endDate);
          return !periodsOverlap(barStart, barEnd, existingStart, existingEnd);
        });
        
        if (levelFree) {
          levelFound = true;
          // Initialiser le niveau s'il n'existe pas
          if (!levels[level]) levels[level] = [];
          levels[level].push(bar);
        } else {
          level++;
        }
      }
      
      positions.push({
        ...bar,
        level,
        top: startOffset + (level * 24) // 24px par niveau (hauteur de barre + marge)
      });
    });

    return positions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du planning des capacités...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      
      <div 
        className="px-4 sm:px-6 lg:px-8 select-none"
        onMouseDown={handleMouseDown}
        style={{
          cursor: isDragging.current ? 'grabbing' : 'grab'
        }}
      >
      {/* En-tête */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Calendar className="h-6 w-6 mr-2 text-primary-600" />
            Capacity Planning
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Vue d'ensemble des affectations des personnes sur les projets
          </p>
        </div>
      </div>

      {/* Informations de la période */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {format(startDate, 'dd MMMM yyyy', { locale: fr })} - {format(endDate, 'dd MMMM yyyy', { locale: fr })}
            </h2>
            <span className="text-sm text-gray-500">
              ({weeksInPeriod.length} semaine{weeksInPeriod.length > 1 ? 's' : ''})
            </span>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-700 font-medium mr-2">Phases:</span>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-400 rounded mr-1"></div>
                <span>Analyse/Design</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-400 rounded mr-1"></div>
                <span>Build</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-400 rounded mr-1"></div>
                <span>Test</span>
              </div>
              <div className="border-l border-gray-300 mx-2 h-4"></div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-orange-400 rounded mr-1"></div>
                <span>Vacances</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grille de calendrier principal */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* En-tête du calendrier */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="flex">
            {/* Colonne des personnes */}
            <div className="flex-shrink-0 p-4 border-r border-gray-200" style={{ width: `${SIDEBAR_WIDTH}px` }}>
              <h3 className="text-sm font-medium text-gray-900 flex items-center">
                <Users className="h-4 w-4 mr-2" />
                Personnes ({personnesData.length})
              </h3>
            </div>
            
            {/* Container avec scroll horizontal pour les semaines */}
            <div 
              ref={headerScrollRef}
              className="flex-1 overflow-x-auto" 
              id="calendar-header-scroll"
              onScroll={(e) => {
                // Synchroniser le scroll avec tous les corps de calendrier
                const bodyScrolls = document.querySelectorAll('.calendar-body-scroll');
                bodyScrolls.forEach(bodyScroll => {
                  bodyScroll.scrollLeft = e.target.scrollLeft;
                });
              }}

            >
              <div className="flex h-16" style={{ width: `${weeksInPeriod.length * COLUMN_WIDTH}px` }}>
                {weeksInPeriod.map((week, index) => {
                  const weekEnd = endOfWeek(week, { weekStartsOn: 1 });
                  return (
                    <div
                      key={index}
                      className="border-r border-gray-200 last:border-r-0 p-1 text-center flex-shrink-0"
                      style={{ width: `${COLUMN_WIDTH}px` }}
                    >
                      <div className="text-xs text-gray-500">
                        Semaine {format(week, 'w', { locale: fr })}
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {format(week, 'd/M', { locale: fr })} - {format(weekEnd, 'd/M', { locale: fr })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Corps du calendrier */}
        <div className="divide-y divide-gray-200">
          {personnesData.map((personne) => (
            <div key={personne.id} className="flex min-h-16">
              {/* Informations de la personne */}
              <div className="flex-shrink-0 p-3 border-r border-gray-200 bg-gray-50" style={{ width: `${SIDEBAR_WIDTH}px` }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-6 w-6 bg-primary-100 rounded-full flex items-center justify-center mr-2">
                      <User className="h-3 w-3 text-primary-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {personne.prenom} {personne.nom}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {personne.role.replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleRattachementClick(personne)}
                      className="flex items-center px-1.5 py-0.5 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                      title="Gérer les rattachements"
                    >
                      <Briefcase className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleVacancesClick(personne)}
                      className="flex items-center px-1.5 py-0.5 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors"
                      title="Gérer les vacances"
                    >
                      <Plane className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <div className="mt-1.5 text-xs text-gray-600 space-y-0.5">
                  {personne.assignments.length > 0 && (
                    <div>
                      {personne.assignments.length} projet{personne.assignments.length > 1 ? 's' : ''}
                    </div>
                  )}
                  {vacancesData[personne.id] && vacancesData[personne.id].length > 0 && (
                    <div className="flex items-center">
                      <Plane className="h-3 w-3 mr-1 text-orange-500" />
                      {vacancesData[personne.id].reduce((total, v) => total + parseFloat(v.jours_vacances), 0)} jour{vacancesData[personne.id].reduce((total, v) => total + parseFloat(v.jours_vacances), 0) > 1 ? 's' : ''} de vacances
                    </div>
                  )}
                  {(() => {
                    const capacite = calculateCapaciteNette(personne);
                    return (
                      <div className="space-y-0.5">
                        <div className="flex items-center">
                          <span className="font-medium text-blue-600">
                            Capacité nette: {capacite.capaciteNette.toFixed(1)}j
                          </span>
                        </div>
                        <div className="text-gray-500">
                          Productivité: {capacite.pourcentageProductivite.toFixed(0)}%
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              
              {/* Timeline des projets et vacances */}
              <div 
                className="flex-1 overflow-x-auto calendar-body-scroll" 
                style={{
                  scrollbarWidth: 'none', /* Firefox */
                  msOverflowStyle: 'none', /* IE et Edge */
                }}
                onScroll={(e) => {
                  const headerScroll = document.getElementById('calendar-header-scroll');
                  if (headerScroll) {
                    headerScroll.scrollLeft = e.target.scrollLeft;
                  }
                }}
  
              >
                <div className="relative" style={{ width: `${weeksInPeriod.length * COLUMN_WIDTH}px`, padding: '8px 0' }}>
                  {(() => {
                    // Préparer les données des barres de vacances pour le calcul des positions
                    const vacancesBars = vacancesData[personne.id] ? vacancesData[personne.id]
                      .map(vacances => {
                        const barStyle = getVacancesBarStyle(vacances.semaine, vacances.jours_vacances);
                        if (barStyle.display === 'none') return null;
                        
                        const weekStart = startOfWeek(parseISO(vacances.semaine), { weekStartsOn: 1 });
                        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
                        
                        return {
                          id: `vacances-${vacances.id}`,
                          type: 'vacances',
                          data: vacances,
                          startDate: weekStart.toISOString().split('T')[0],
                          endDate: weekEnd.toISOString().split('T')[0],
                          barStyle
                        };
                      })
                      .filter(Boolean) : [];

                    // Préparer les données des barres de projets pour le calcul des positions
                    const projectBars = personne.assignments
                      .map(assignment => {
                        if (!assignment.projet) return null;
                        
                        const barStyle = getProjectBarStyle(
                          assignment.date_debut || assignment.projet.date_debut,
                          assignment.date_fin || assignment.projet.date_fin
                        );
                        
                        if (barStyle.display === 'none') return null;
                        
                        return {
                          id: assignment.id,
                          type: 'project',
                          data: assignment,
                          startDate: assignment.date_debut || assignment.projet.date_debut,
                          endDate: assignment.date_fin || assignment.projet.date_fin,
                          barStyle
                        };
                      })
                      .filter(Boolean);

                                         // Calculer les positions des barres de projets séparément
                     const positionedProjectBars = calculateBarPositions(projectBars, 16); // Commencer après la barre de capacité
                     
                     // Calculer les positions des barres de vacances séparément (toujours en bas)
                     const positionedVacancesBars = vacancesBars.map((bar, index) => ({
                       ...bar,
                       level: 0, // Toutes les vacances sur le même niveau
                       top: 16 + (positionedProjectBars.length > 0 ? Math.max(...positionedProjectBars.map(b => b.level)) + 1 : 0) * 24 + 4 // Après les projets + marge
                     }));
                     
                     // Combiner les barres positionnées
                     const positionedBars = [...positionedProjectBars, ...positionedVacancesBars];

                     // Calculer la hauteur dynamique nécessaire
                     const maxProjectLevel = positionedProjectBars.length > 0 ? Math.max(...positionedProjectBars.map(bar => bar.level)) : 0;
                     const hasVacances = positionedVacancesBars.length > 0;
                     const dynamicHeight = Math.max(80, 16 + (maxProjectLevel + 1) * 24 + (hasVacances ? 24 + 4 : 8) + 50); // +50px pour le graphique de charge

                    return (
                      <div className="relative" style={{ height: `${dynamicHeight}px` }}>
                        {/* Grille de fond */}
                        <div className="absolute inset-0 flex">
                          {weeksInPeriod.map((week, index) => (
                            <div
                              key={index}
                              className="border-r border-gray-100 last:border-r-0 flex-shrink-0"
                              style={{ width: `${COLUMN_WIDTH}px` }}
                            />
                          ))}
                                                     {(() => {
                           const chargeData = calculateChargeParSemaine(personne);
                                                      return (
                              <div
                                className="absolute bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden"
                                style={{
                                  left: '0%',
                                  width: '100%',
                                  top: `${dynamicHeight - 50}px`,
                                  height: '50px',
                                  zIndex: 5
                                }}
                              >
                                {/* Arrière-plan avec couleurs dynamiques */}
                                <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 5 }}>
                                  <defs>
                                    <filter id="blur">
                                      <feGaussianBlur stdDeviation="2" />
                                    </filter>
                                  </defs>
                                  {chargeData.backgroundColors.map((bg, index) => {
                                    const nextBg = chargeData.backgroundColors[index + 1];
                                    if (!nextBg) return null;
                                    
                                    // Créer un rectangle entre les points avec une couleur interpolée
                                    const width = nextBg.x - bg.x;
                                    const height = 50;
                                    
                                    return (
                                      <rect
                                        key={`bg-${index}`}
                                        x={bg.x}
                                        y={0}
                                        width={width}
                                        height={height}
                                        fill={bg.color}
                                        opacity="0.3"
                                        filter="url(#blur)"
                                      />
                                    );
                                  })}
                                </svg>
                                
                                {/* Courbe de charge */}
                                <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 10 }}>
                                  <polyline
                                    points={chargeData.points}
                                    fill="none"
                                    stroke="#3b82f6"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  {/* Points sur la courbe */}
                                  {chargeData.points.split(' ').map((point, index) => {
                                    const [x, y] = point.split(',').map(Number);
                                    return (
                                      <circle
                                        key={index}
                                        cx={x}
                                        cy={y}
                                        r="2"
                                        fill="#3b82f6"
                                        stroke="white"
                                        strokeWidth="1"
                                      />
                                    );
                                  })}
                                </svg>
                                
                                {/* Titre du graphique */}
                                <div className="absolute top-1 left-2 text-xs font-medium text-gray-700">
                                  📈 Charge par semaine
                                </div>
                              </div>
                            );
                         })()}
                        </div>
                        
                        {/* Graphique de charge par semaine */}
                        
                        
                                                 {/* Barres de projets positionnées sans chevauchement */}
                         {positionedProjectBars.map((bar) => {
                           // Barres de projet
                           const assignment = bar.data;
                           
                           // Utiliser la couleur de la phase du rattachement (ou du projet si pas de phase)
                           const colorClass = assignment.phase 
                             ? getPhaseColor(assignment.phase)
                             : getProjectColor(assignment.projet.statut);
                           
                           // Calculer les temps planifiés pour cette phase
                           const tempsPlanifies = assignment.phase 
                             ? calculateTempsPlanifiesForPhase(assignment, assignment.phase)
                             : 0;
                           
                           // Obtenir la couleur de bordure correspondante
                           const borderColorClass = assignment.phase 
                             ? assignment.phase === 'analyse_design' ? 'border-purple-600' :
                               assignment.phase === 'build' ? 'border-blue-600' : 'border-green-600'
                             : assignment.projet.statut === 'planifie' ? 'border-blue-600' :
                               assignment.projet.statut === 'en_cours' ? 'border-green-600' :
                               assignment.projet.statut === 'termine' ? 'border-gray-600' : 'border-red-600';
                           
                           return (
                             <div
                               key={bar.id}
                               className={`absolute h-5 rounded-md ${colorClass} border-2 ${borderColorClass} opacity-80 hover:opacity-100 cursor-pointer shadow-sm transition-opacity`}
                               style={{
                                 ...bar.barStyle,
                                 top: `${bar.top}px`,
                                 zIndex: 10 + bar.level
                               }}
                               title={`${assignment.projet.nom} (${assignment.role_projet})${assignment.phase ? ` - Phase: ${assignment.phase}` : ''}${tempsPlanifies > 0 ? ` - ${tempsPlanifies.toFixed(1)}j planifiés` : ''}`}
                             >
                               <div className="px-2 py-0.5 text-xs text-white font-medium truncate">
                                 {assignment.projet.nom}
                                 {tempsPlanifies > 0 && (
                                   <span className="ml-1 font-bold">
                                     ({tempsPlanifies.toFixed(1)}j)
                                   </span>
                                 )}
                               </div>
                             </div>
                           );
                                                   })}
                          
                          {/* Barres de vacances toujours en bas sur la même ligne */}
                          {positionedVacancesBars.map((bar, index) => {
                            // Calculer un décalage horizontal pour éviter les chevauchements visuels
                            const horizontalOffset = index * 2; // Décalage de 2% par barre de vacances
                            
                            return (
                              <div
                                key={bar.id}
                                className="absolute h-5 rounded-md bg-orange-400 border-2 border-orange-500 opacity-90 hover:opacity-100 cursor-pointer shadow-sm transition-opacity"
                                style={{
                                  ...bar.barStyle,
                                  left: `calc(${bar.barStyle.left} + ${horizontalOffset}%)`,
                                  width: `calc(${bar.barStyle.width} - ${horizontalOffset}%)`,
                                  top: `${bar.top}px`,
                                  zIndex: 20 + index // Z-index croissant pour les vacances
                                }}
                                title={`Vacances: ${bar.data.jours_vacances} jour${bar.data.jours_vacances > 1 ? 's' : ''} ${bar.data.commentaire ? `- ${bar.data.commentaire}` : ''}`}
                              >
                                <div className="px-1 py-0.5 text-xs text-white font-medium truncate">
                                  ✈️ {bar.data.jours_vacances}j
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Barres de vacances toujours en bas sur la même ligne */}
                          {positionedVacancesBars.map((bar, index) => {
                            // Calculer un décalage horizontal pour éviter les chevauchements visuels
                            const horizontalOffset = index * 2; // Décalage de 2% par barre de vacances
                            
                            return (
                              <div
                                key={bar.id}
                                className="absolute h-5 rounded-md bg-orange-400 border-2 border-orange-500 opacity-90 hover:opacity-100 cursor-pointer shadow-sm transition-opacity"
                                style={{
                                  ...bar.barStyle,
                                  left: `calc(${bar.barStyle.left} + ${horizontalOffset}%)`,
                                  width: `calc(${bar.barStyle.width} - ${horizontalOffset}%)`,
                                  top: `${bar.top}px`,
                                  zIndex: 20 + index // Z-index croissant pour les vacances
                                }}
                                title={`Vacances: ${bar.data.jours_vacances} jour${bar.data.jours_vacances > 1 ? 's' : ''} ${bar.data.commentaire ? `- ${bar.data.commentaire}` : ''}`}
                              >
                                <div className="px-1 py-0.5 text-xs text-white font-medium truncate">
                                  ✈️ {bar.data.jours_vacances}j
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Message si pas d'affectation ni de vacances */}
                          {personne.assignments.length === 0 && (!vacancesData[personne.id] || vacancesData[personne.id].length === 0) && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-sm text-gray-400 italic">
                                Aucune affectation
                              </span>
                            </div>
                          )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          ))}
          
          {/* Ligne de total */}
          {(() => {
            const capaciteTotale = calculateCapaciteTotale();
            return (
              <div className="flex bg-primary-50 border-t-2 border-primary-200">
                {/* Colonne des totaux */}
                <div className="flex-shrink-0 p-4 border-r border-gray-200" style={{ width: `${SIDEBAR_WIDTH}px` }}>
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-primary-200 rounded-full flex items-center justify-center mr-3">
                      <Users className="h-4 w-4 text-primary-700" />
                    </div>
                    <div>
                      <div className="font-bold text-primary-900">
                        TOTAL ÉQUIPE
                      </div>
                      <div className="text-sm text-primary-700">
                        {personnesData.length} personne{personnesData.length > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-primary-700 space-y-1">
                    <div className="font-semibold">
                      Capacité nette: {capaciteTotale.capaciteNette.toFixed(1)} jours
                    </div>
                    <div className="text-xs space-y-0.5">
                      <div>Théorique: {capaciteTotale.capaciteTheorique.toFixed(1)}j</div>
                      <div>Avec productivité: {capaciteTotale.capaciteAvecProductivite.toFixed(1)}j</div>
                      <div>Vacances: -{capaciteTotale.joursVacances.toFixed(1)}j</div>
                    </div>
                  </div>
                </div>
                
                {/* Barre de capacité totale */}
                <div 
                  className="flex-1 overflow-x-auto calendar-body-scroll" 
                  style={{
                    scrollbarWidth: 'none', /* Firefox */
                    msOverflowStyle: 'none', /* IE et Edge */
                  }}
                  onScroll={(e) => {
                    const headerScroll = document.getElementById('calendar-header-scroll');
                    if (headerScroll) {
                      headerScroll.scrollLeft = e.target.scrollLeft;
                    }
                  }}
    
                >
                  <div className="relative" style={{ width: `${weeksInPeriod.length * COLUMN_WIDTH}px`, padding: '8px 0' }}>
                    <div className="relative h-20">
                      {/* Grille de fond */}
                      <div className="absolute inset-0 flex">
                        {weeksInPeriod.map((week, index) => (
                          <div
                            key={index}
                            className="border-r border-gray-100 last:border-r-0 flex-shrink-0"
                            style={{ width: `${COLUMN_WIDTH}px` }}
                          />
                        ))}
                      </div>
                      
                      {/* Barre de capacité totale */}
                      <div
                        className="absolute h-6 rounded-md bg-primary-300 border-2 border-primary-500 opacity-80 shadow-sm"
                        style={{
                          left: '0%',
                          width: '100%',
                          top: '8px',
                          zIndex: 5
                        }}
                        title={`Capacité totale de l'équipe: ${capaciteTotale.capaciteNette.toFixed(1)} jours`}
                      >
                        <div className="px-2 py-1 text-sm text-primary-900 font-bold truncate text-center">
                          📊 TOTAL: {capaciteTotale.capaciteNette.toFixed(1)} jours
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Résumé des projets */}
      <div className="mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Projets de la période
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projetsWithRattachements
            .filter(projet => {
              const projectStart = parseISO(projet.date_debut);
              const projectEnd = parseISO(projet.date_fin);
              return projectEnd >= startDate && projectStart <= endDate;
            })
            .map(projet => (
              <div key={projet.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 truncate">
                    {projet.nom}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleProjetRattachementClick(projet)}
                      className="flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                      title="Gérer l'équipe"
                    >
                      <Users className="h-3 w-3" />
                    </button>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      projet.statut === 'planifie' ? 'bg-blue-100 text-blue-800' :
                      projet.statut === 'en_cours' ? 'bg-green-100 text-green-800' :
                      projet.statut === 'termine' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {projet.statut}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  {format(parseISO(projet.date_debut), 'dd/MM/yyyy')} - {format(parseISO(projet.date_fin), 'dd/MM/yyyy')}
                </div>
                {projet.rattachements.length > 0 && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="h-4 w-4 mr-1" />
                    {projet.rattachements.length} personne{projet.rattachements.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Modal de gestion des rattachements */}
      <RattachementModal
        isOpen={showRattachementModal}
        onClose={handleCloseRattachementModal}
        personne={selectedPersonneForRattachement}
        onRattachementsUpdated={() => {
          // Recharger les données
          loadCapacityData();
        }}
      />

      {/* Modal de gestion de l'équipe projet */}
      <ProjetRattachementModal
        isOpen={showProjetRattachementModal}
        onClose={handleCloseProjetRattachementModal}
        projet={selectedProjetForRattachement}
        onRattachementsUpdated={() => {
          // Recharger les données
          loadCapacityData();
        }}
      />

      {/* Modal de gestion des vacances */}
      <VacancesModal
        isOpen={showVacancesModal}
        onClose={handleCloseVacancesModal}
        personne={selectedPersonneForVacances}
        onVacancesUpdated={() => {
          // Recharger les données si nécessaire
          loadCapacityData();
        }}
      />
      </div>
    </>
  );
};

export default CapacityPlanning;
